import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowForward from "@mui/icons-material/ArrowForward";
import Check from "@mui/icons-material/Check";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Close from "@mui/icons-material/Close";
import EditOutlined from "@mui/icons-material/EditOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import Launch from "@mui/icons-material/Launch";
import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import PublicOutlined from "@mui/icons-material/PublicOutlined";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import Star from "@mui/icons-material/Star";
import StarBorder from "@mui/icons-material/StarBorder";
import WarningAmber from "@mui/icons-material/WarningAmber";
import axios from "axios";
import apiService from "../../../../service/Api";
import type { VenueData } from "../../types/venues.types";

/* =========================================================
   TYPES
   ========================================================= */

type PublicLayout = {
  id: number;
  layout_type: string;
  capacity: number | string;
};

type PublicationAction = "save" | "publish" | "unpublish";

type UnknownRecord = Record<string, unknown>;

type ReadinessItem = {
  name: string;
  value: string;
  valid: boolean;
  source: "venue" | "listing";
  optional?: boolean;
};

export interface Publication {
  is_published: boolean;
  can_publish: boolean;
  slug: string;
  public_url: string | null;
  published_at: string | null;

  // Read-only values sourced from the main Venue record.
  name: string;
  address: string;
  city: string;
  suburb: string;
  country: string;
  venue_type: string;
  capacity: number | null;
  latitude: number | null;
  longitude: number | null;

  // Read-only: everything below comes straight from the venue at read time.
  description: string;
  amenities: string[];
  layouts: PublicLayout[];
  contact_phone: string;
  contact_email: string;

  // The only thing actually chosen on the listing itself.
  cover_photo_id: number | null;
}

interface VenuePublicationProps {
  venueData: VenueData;
  onEditVenue?: () => void;
}

/* =========================================================
   CONSTANTS
   ========================================================= */

const STEPS = [
  "Listing details",
  "Photos & features",
  "Review & publish",
] as const;

const UNSUPPORTED_PUBLIC_TYPES = new Set([
  "private_residence",
  "family_home",
  "blank_one",
  "blank_two",
]);

/* =========================================================
   HELPERS
   ========================================================= */

function asRecord(value: unknown): UnknownRecord {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as UnknownRecord;
  }
  return {};
}

function formatLabel(value: string) {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function isTrueLike(value: unknown) {
  if (value === true) {
    return true;
  }

  if (typeof value !== "string") {
    return false;
  }

  return ["yes", "true", "1"].includes(value.trim().toLowerCase());
}

function clonePublication(publication: Publication): Publication {
  return {
    ...publication,
    amenities: [...publication.amenities],
    layouts: publication.layouts.map((layout) => ({ ...layout })),
  };
}

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error) && error.response?.data) {
    const describe = (value: unknown): string => {
      if (Array.isArray(value)) {
        return value.map(describe).join(" ");
      }

      if (value && typeof value === "object") {
        return Object.entries(value)
          .map(([key, item]) => `${formatLabel(key)}: ${describe(item)}`)
          .join("\n");
      }

      return String(value);
    };

    return describe(error.response.data);
  }

  return "Could not update the website listing. Please try again.";
}

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

export default function VenuePublication({
  venueData,
  onEditVenue,
}: VenuePublicationProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const endpoint = `/api/venues/venue/${venueData.id}/publication/`;

  const [publication, setPublication] = useState<Publication | null>(null);
  const [draft, setDraft] = useState<Publication | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [maxVisitedStep, setMaxVisitedStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [retry, setRetry] = useState(0);
  const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
  const busy = useRef(false);

  useEffect(() => {
    let active = true;

    setLoading(true);
    setLoadError(false);

    apiService
      .get<Publication>(endpoint)
      .then(({ data }) => {
        if (active) {
          setPublication(data);
        }
      })
      .catch(() => {
        if (active) {
          setLoadError(true);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [endpoint, retry]);

  const photos = useMemo(
    () =>
      (venueData.files ?? []).filter(
        (file) =>
          ["venue_photos", "room_photos"].includes(file.file_type) &&
          /\.(jpe?g|png|webp|gif|avif)(?:[?#]|$)/i.test(file.file),
      ),
    [venueData.files],
  );

  const listingStatus = publication?.is_published
    ? "Published"
    : publication?.slug
      ? "Draft"
      : "Unpublished";

  const handleOpen = () => {
    if (!publication) {
      return;
    }

    const copy = clonePublication(publication);

    // The cover choice is the only thing actually stored on the listing, so it's
    // the only thing that can go stale (e.g. the photo was removed since last saved).
    if (
      copy.cover_photo_id !== null &&
      !photos.some((photo) => photo.id === copy.cover_photo_id)
    ) {
      copy.cover_photo_id = null;
    }

    setDraft(copy);
    setStep(0);
    setMaxVisitedStep(publication.is_published ? 2 : 0);
    setError("");
    setNotice("");
    setDialogOpen(true);
  };

  const handleClose = () => {
    if (saving) {
      return;
    }

    setDialogOpen(false);
    setDraft(null);
    setStep(0);
    setError("");
  };

  const handleEditVenue = () => {
    handleClose();
    onEditVenue?.();
  };

  const goToStep = (requestedStep: number) => {
    const nextStep = Math.max(0, Math.min(2, requestedStep));
    setStep(nextStep);
    setMaxVisitedStep((current) => Math.max(current, nextStep));
  };

  const updateDraft = <K extends keyof Publication>(
    key: K,
    value: Publication[K],
  ) => {
    setDraft((current) =>
      current
        ? {
            ...current,
            [key]: value,
          }
        : current,
    );
  };

  const setCoverPhoto = (photoId: number) => {
    updateDraft("cover_photo_id", photoId);
  };

  const hasPublicContact = Boolean(
    draft?.contact_phone.trim() || draft?.contact_email.trim(),
  );

  const venueRecord = asRecord(venueData);
  const isPrivateVenue =
    isTrueLike(venueRecord.is_private_residence) ||
    draft?.venue_type === "private_residence" ||
    draft?.venue_type === "family_home";

  const parentValue = venueRecord.parent_id ?? venueRecord.parent;
  const isChildVenue =
    parentValue !== undefined && parentValue !== null && parentValue !== false;

  const readiness: ReadinessItem[] = draft
    ? [
        {
          name: "Venue name",
          value: draft.name || "Missing",
          valid: Boolean(draft.name.trim()),
          source: "venue",
        },
        {
          name: "Location",
          value:
            [draft.suburb, draft.city].filter(Boolean).join(", ") || "Missing",
          valid: Boolean(draft.city.trim()),
          source: "venue",
        },
        {
          name: "Venue type",
          value: draft.venue_type ? formatLabel(draft.venue_type) : "Missing",
          valid:
            Boolean(draft.venue_type) &&
            !UNSUPPORTED_PUBLIC_TYPES.has(draft.venue_type),
          source: "venue",
        },
        {
          name: "Capacity",
          value: draft.capacity ? `${draft.capacity} guests` : "Missing",
          valid:
            draft.capacity !== null &&
            draft.capacity >= 1 &&
            draft.capacity <= 1_000_000,
          source: "venue",
        },
        {
          name: "Public description",
          value: draft.description.trim() ? "Added" : "Missing",
          valid: Boolean(draft.description.trim()),
          source: "venue",
        },
        {
          name: "Cover photo",
          value: draft.cover_photo_id !== null ? "Verified" : "Missing",
          valid: draft.cover_photo_id !== null,
          source: "listing",
        },
        {
          name: "Venue eligibility",
          value: !isPrivateVenue && !isChildVenue ? "Verified" : "Not eligible",
          valid: !isPrivateVenue && !isChildVenue,
          source: "venue",
        },
        {
          name: "Public contact",
          value: hasPublicContact ? "Shown" : "Not shown",
          valid: true,
          optional: true,
          source: "listing",
        },
      ]
    : [];

  const readyToPublish = readiness
    .filter((item) => !item.optional)
    .every((item) => item.valid);

  const selectedCoverPhoto = draft
    ? photos.find((photo) => photo.id === draft.cover_photo_id)
    : undefined;

  const galleryPreview = draft
    ? photos
        .filter((photo) => photo.id !== draft.cover_photo_id)
        .slice(0, 3)
    : [];

  const submit = async (action: PublicationAction) => {
    if (busy.current || !publication?.can_publish) {
      return;
    }

    if (action !== "unpublish" && !draft) {
      return;
    }

    busy.current = true;
    setSaving(true);
    setError("");
    setNotice("");

    try {
      const payload =
        action === "unpublish"
          ? { action }
          : { action, cover_photo_id: draft!.cover_photo_id };

      const { data } = await apiService.put<Publication>(endpoint, payload);
      setPublication(data);

      if (action === "save") {
        setDraft(clonePublication(data));
        setNotice(
          data.is_published ? "Live website changes saved." : "Draft saved.",
        );
        return;
      }

      if (action === "publish") {
        setNotice("Venue published successfully.");
        setDialogOpen(false);
        setDraft(null);
        return;
      }

      setNotice("Venue unpublished successfully.");
      setUnpublishDialogOpen(false);
      setDialogOpen(false);
      setDraft(null);
    } catch (failure) {
      setError(getErrorMessage(failure));
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Loading website listing…
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (loadError || !publication) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Website Listing
        </Typography>
        <Alert
          severity="warning"
          action={
            <Button size="small" onClick={() => setRetry((value) => value + 1)}>
              Retry
            </Button>
          }
        >
          Status unavailable.
        </Alert>
      </Paper>
    );
  }

  return (
    <>
      {/* =====================================================
          WEBSITE LISTING CARD — place this in the View Venue
          right sidebar above the Primary Contact card.
          ===================================================== */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 1,
            color: "text.secondary",
            textTransform: "uppercase",
          }}
        >
          Website Listing
        </Typography>

        <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mt: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: publication.is_published
                ? "success.main"
                : publication.slug
                  ? "warning.main"
                  : "text.disabled",
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" fontWeight={700}>
            {listingStatus}
          </Typography>
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5 }}
        >
          {publication.is_published
            ? "This venue is visible on the website."
            : publication.slug
              ? "Continue reviewing this draft before publishing."
              : "This venue is not visible on the website."}
        </Typography>

        {publication.can_publish && (
          <Button
            fullWidth
            size="small"
            variant="contained"
            startIcon={<PublicOutlined />}
            onClick={handleOpen}
            sx={{ mt: 1.5, textTransform: "none" }}
          >
            {publication.is_published
              ? "Manage listing"
              : publication.slug
                ? "Continue publishing"
                : "Publish to website"}
          </Button>
        )}

        {publication.is_published &&
          publication.public_url &&
          /^https?:\/\//i.test(publication.public_url) && (
            <Button
              fullWidth
              size="small"
              variant="text"
              startIcon={<Launch />}
              href={publication.public_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mt: 0.5, textTransform: "none" }}
            >
              View on website
            </Button>
          )}

        {notice && !dialogOpen && (
          <Alert
            severity="success"
            icon={false}
            sx={{ mt: 1.25, py: 0.25, px: 1, fontSize: 11 }}
            onClose={() => setNotice("")}
          >
            {notice}
          </Alert>
        )}
      </Paper>

      {/* =====================================================
          PUBLISHING MODAL
          ===================================================== */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        fullScreen={fullScreen}
        slotProps={{
          paper: {
            sx: {
              height: { xs: "100%", sm: "90vh" },
              maxHeight: { xs: "100%", sm: "90vh" },
              borderRadius: { xs: 0, sm: 2.5 },
              overflow: "hidden",
            },
          },
        }}
      >
        {draft && (
          <>
            {/* HEADER */}
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                pt: 2,
                pb: 1.5,
                bgcolor: "background.paper",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    color="primary.main"
                    fontWeight={700}
                    letterSpacing={1.2}
                    sx={{ lineHeight: 1.2 }}
                  >
                    WEBSITE LISTING
                  </Typography>

                  <Box
                    sx={{
                      mt: 0.35,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="h6" fontWeight={700}>
                      {publication.is_published
                        ? "Manage your listing"
                        : "Publish to website"}
                    </Typography>
                    <Typography color="text.secondary">•</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      {draft.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={listingStatus}
                      color={publication.is_published ? "success" : "default"}
                      sx={{ height: 22, fontSize: 10 }}
                    />
                  </Box>
                </Box>

                <IconButton
                  aria-label="Close publishing dialog"
                  disabled={saving}
                  onClick={handleClose}
                  size="small"
                >
                  <Close />
                </IconButton>
              </Box>

              {/* COMPACT STEPPER */}
              <PublicationStepper
                step={step}
                maxVisitedStep={maxVisitedStep}
                onStepChange={goToStep}
              />
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{ mx: { xs: 2, sm: 3 }, mt: 1.5, whiteSpace: "pre-line" }}
              >
                {error}
              </Alert>
            )}

            {notice && (
              <Alert
                severity="success"
                sx={{ mx: { xs: 2, sm: 3 }, mt: 1.5 }}
                onClose={() => setNotice("")}
              >
                {notice}
              </Alert>
            )}

            <DialogContent sx={{ p: 0, overflowY: "auto", bgcolor: "#F7F8FC" }}>
              {/* =================================================
                  STEP 1 — LISTING DETAILS
                  ================================================= */}
              {step === 0 && (
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  <Stack spacing={3}>
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-end",
                          flexWrap: "wrap",
                          gap: 2,
                          mb: 1.5,
                        }}
                      >
                        <Box>
                          <Typography fontWeight={700}>
                            Venue details
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            These details come from the main venue record.
                          </Typography>
                        </Box>

                        {onEditVenue && (
                          <Button
                            size="small"
                            endIcon={<EditOutlined />}
                            onClick={handleEditVenue}
                            sx={{ textTransform: "none" }}
                          >
                            Edit venue details
                          </Button>
                        )}
                      </Box>

                      <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "repeat(2, minmax(0, 1fr))",
                              md: "repeat(3, minmax(0, 1fr))",
                            },
                            gap: 2.5,
                          }}
                        >
                          <ReadOnlyItem
                            label="Venue name"
                            value={draft.name || "—"}
                          />
                          <ReadOnlyItem
                            label="Venue type"
                            value={
                              draft.venue_type
                                ? formatLabel(draft.venue_type)
                                : "—"
                            }
                          />
                          <ReadOnlyItem
                            label="Capacity"
                            value={
                              draft.capacity
                                ? `Up to ${draft.capacity} guests`
                                : "Needs attention"
                            }
                          />
                          <ReadOnlyItem
                            label="Location"
                            value={
                              [draft.suburb, draft.city]
                                .filter(Boolean)
                                .join(", ") || "—"
                            }
                          />
                          <Box sx={{ gridColumn: { sm: "span 2" } }}>
                            <ReadOnlyItem
                              label="Address"
                              value={draft.address || "—"}
                            />
                          </Box>
                        </Box>
                      </Paper>
                    </Box>

                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-end",
                          flexWrap: "wrap",
                          gap: 2,
                          mb: 1.25,
                        }}
                      >
                        <Box>
                          <Typography fontWeight={700}>
                            About the venue
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            This is the description visitors will see on the
                            website.
                          </Typography>
                        </Box>

                        {onEditVenue && (
                          <Button
                            size="small"
                            endIcon={<EditOutlined />}
                            onClick={handleEditVenue}
                            sx={{ textTransform: "none" }}
                          >
                            Edit venue details
                          </Button>
                        )}
                      </Box>

                      <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
                        <Typography
                          variant="body2"
                          color={draft.description ? "text.primary" : "text.secondary"}
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {draft.description ||
                            "No description has been added to this venue yet. Add one from Edit Venue."}
                        </Typography>
                      </Paper>
                    </Box>

                    {/* PUBLIC CONTACT */}
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-end",
                          flexWrap: "wrap",
                          gap: 2,
                          mb: 1.25,
                        }}
                      >
                        <Box>
                          <Typography fontWeight={700}>
                            Public contact
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            This is the venue&apos;s primary contact, shown to
                            visitors when they enquire about booking.
                          </Typography>
                        </Box>

                        {onEditVenue && (
                          <Button
                            size="small"
                            endIcon={<EditOutlined />}
                            onClick={handleEditVenue}
                            sx={{ textTransform: "none" }}
                          >
                            Edit venue details
                          </Button>
                        )}
                      </Box>

                      <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
                        {hasPublicContact ? (
                          <Stack spacing={0.25}>
                            {draft.contact_phone && (
                              <Typography variant="body2" fontWeight={600}>
                                {draft.contact_phone}
                              </Typography>
                            )}
                            {draft.contact_email && (
                              <Typography variant="body2" color="primary.main">
                                {draft.contact_email}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Box
                            sx={{
                              py: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              color: "text.secondary",
                            }}
                          >
                            <RadioButtonUnchecked sx={{ fontSize: 18 }} />
                            <Typography variant="body2">
                              This venue has no contact with a phone or email
                              yet, so none will appear on the listing.
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  </Stack>
                </Box>
              )}

              {/* =================================================
                  STEP 2 — PHOTOS & FEATURES
                  ================================================= */}
              {step === 1 && (
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  <Stack spacing={4}>
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 2,
                          flexWrap: "wrap",
                          mb: 1.5,
                        }}
                      >
                        <Box>
                          <Typography fontWeight={700}>
                            Cover photo
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Every venue photo appears in the website gallery
                            automatically — choose which one leads as the
                            cover.
                          </Typography>
                        </Box>
                      </Box>

                      {photos.length === 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          No suitable venue or room photos are available. Upload
                          photos to the venue before publishing.
                        </Alert>
                      )}

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "repeat(2, minmax(0, 1fr))",
                            sm: "repeat(3, minmax(0, 1fr))",
                            md: "repeat(4, minmax(0, 1fr))",
                          },
                          gap: 1.5,
                        }}
                      >
                        {photos.map((photo) => {
                          const isCover = draft.cover_photo_id === photo.id;

                          return (
                            <Paper
                              key={photo.id}
                              variant="outlined"
                              sx={{
                                overflow: "hidden",
                                borderRadius: 2,
                                borderColor: isCover ? "primary.main" : "divider",
                                borderWidth: isCover ? 2 : 1,
                              }}
                            >
                              <ButtonBase
                                onClick={() => setCoverPhoto(photo.id)}
                                sx={{
                                  width: "100%",
                                  display: "block",
                                  aspectRatio: "4 / 3",
                                  position: "relative",
                                  bgcolor: "action.hover",
                                }}
                              >
                                <Box
                                  component="img"
                                  src={photo.file}
                                  alt={`${draft.name} venue`}
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    display: "block",
                                    objectFit: "cover",
                                  }}
                                />

                                {isCover && (
                                  <Chip
                                    size="small"
                                    icon={<Star sx={{ fontSize: 14 }} />}
                                    label="Cover"
                                    color="primary"
                                    sx={{
                                      position: "absolute",
                                      top: 8,
                                      left: 8,
                                      textTransform: "uppercase",
                                      fontWeight: 700,
                                    }}
                                  />
                                )}
                              </ButtonBase>

                              <Box sx={{ p: 0.75 }}>
                                <Button
                                  fullWidth
                                  size="small"
                                  variant={isCover ? "contained" : "text"}
                                  disabled={isCover}
                                  startIcon={isCover ? <Star /> : <StarBorder />}
                                  onClick={() => setCoverPhoto(photo.id)}
                                  sx={{ textTransform: "none" }}
                                >
                                  {isCover ? "Cover photo" : "Set as cover"}
                                </Button>
                              </Box>
                            </Paper>
                          );
                        })}
                      </Box>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography fontWeight={700}>Amenities</Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        These are the amenities set on the venue and will show
                        on the website.
                      </Typography>

                      {draft.amenities.length === 0 ? (
                        <Alert
                          severity="info"
                          action={
                            onEditVenue && (
                              <Button
                                size="small"
                                onClick={handleEditVenue}
                                sx={{ textTransform: "none" }}
                              >
                                Edit venue
                              </Button>
                            )
                          }
                        >
                          No amenities have been added to this venue yet.
                        </Alert>
                      ) : (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                        >
                          {draft.amenities.map((amenity) => (
                            <Chip key={amenity} label={amenity} />
                          ))}
                        </Box>
                      )}
                    </Box>

                    <Divider />

                    <Box>
                      <Typography fontWeight={700}>
                        Spaces &amp; layouts
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        These are the layouts and capacities set on the venue
                        and will show on the website.
                      </Typography>

                      {draft.layouts.length === 0 ? (
                        <Alert
                          severity="info"
                          action={
                            onEditVenue && (
                              <Button
                                size="small"
                                onClick={handleEditVenue}
                                sx={{ textTransform: "none" }}
                              >
                                Edit venue
                              </Button>
                            )
                          }
                        >
                          No layouts have been added to this venue yet.
                        </Alert>
                      ) : (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                        >
                          {draft.layouts.map((layout) => (
                            <Chip
                              key={layout.id}
                              label={`${formatLabel(layout.layout_type)} · ${layout.capacity} guests`}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>

                  </Stack>
                </Box>
              )}

              {/* =================================================
                  STEP 3 — REVIEW & PUBLISH
                  ================================================= */}
              {step === 2 && (
                <Box
                  sx={{
                    p: { xs: 2, sm: 3 },
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "minmax(0, 1.25fr) minmax(320px, .75fr)",
                    },
                    gap: 3,
                    alignItems: "start",
                  }}
                >
                  <Box>
                    <Typography fontWeight={700}>Listing preview</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      Preview the public information selected for this venue.
                    </Typography>

                    <Paper
                      variant="outlined"
                      sx={{ borderRadius: 2, overflow: "hidden" }}
                    >
                      <Box
                        sx={{
                          height: { xs: 190, sm: 230 },
                          position: "relative",
                          bgcolor: "action.hover",
                        }}
                      >
                        {selectedCoverPhoto ? (
                          <Box
                            component="img"
                            src={selectedCoverPhoto.file}
                            alt={draft.name}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: "100%",
                              display: "grid",
                              placeItems: "center",
                              color: "text.secondary",
                            }}
                          >
                            Choose a cover photo
                          </Box>
                        )}

                        <Chip
                          size="small"
                          label={
                            draft.venue_type
                              ? formatLabel(draft.venue_type)
                              : "Venue"
                          }
                          sx={{
                            position: "absolute",
                            top: 12,
                            left: 12,
                            bgcolor: "rgba(0,0,0,.72)",
                            color: "#fff",
                          }}
                        />

                        {draft.capacity && (
                          <Chip
                            icon={<GroupsOutlined />}
                            size="small"
                            label={`Up to ${draft.capacity} guests`}
                            sx={{
                              position: "absolute",
                              right: 12,
                              bottom: 12,
                              bgcolor: "rgba(255,255,255,.94)",
                            }}
                          />
                        )}
                      </Box>

                      <Box sx={{ p: 2 }}>
                        <Typography variant="h6" fontWeight={700}>
                          {draft.name}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          <LocationOnOutlined
                            sx={{ fontSize: 17 }}
                            color="primary"
                          />
                          <Typography variant="body2" color="text.secondary">
                            {[draft.suburb, draft.city]
                              .filter(Boolean)
                              .join(", ") || "Location unavailable"}
                          </Typography>
                        </Stack>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 1.5,
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 3,
                            overflow: "hidden",
                          }}
                        >
                          {draft.description ||
                            "No public description has been added."}
                        </Typography>

                        {draft.amenities.length > 0 && (
                          <Box
                            sx={{
                              mt: 1.5,
                              display: "flex",
                              gap: 0.7,
                              flexWrap: "wrap",
                            }}
                          >
                            {draft.amenities.slice(0, 4).map((amenity) => (
                              <Chip
                                key={amenity}
                                size="small"
                                label={amenity}
                              />
                            ))}
                            {draft.amenities.length > 4 && (
                              <Chip
                                size="small"
                                label={`+${draft.amenities.length - 4} more`}
                              />
                            )}
                          </Box>
                        )}

                        {draft.layouts.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={700}
                            >
                              SPACES & LAYOUTS
                            </Typography>
                            <Box
                              sx={{
                                mt: 0.75,
                                display: "flex",
                                gap: 0.7,
                                flexWrap: "wrap",
                              }}
                            >
                              {draft.layouts
                                .slice(0, 3)
                                .map((layout) => (
                                  <Chip
                                    key={layout.id}
                                    size="small"
                                    variant="outlined"
                                    label={`${formatLabel(layout.layout_type)} · ${layout.capacity} guests`}
                                  />
                                ))}
                              {draft.layouts.length > 3 && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`+${draft.layouts.length - 3} more`}
                                />
                              )}
                            </Box>
                          </Box>
                        )}

                        {hasPublicContact && (
                          <Box
                            sx={{
                              mt: 2,
                              pt: 1.5,
                              borderTop: 1,
                              borderColor: "divider",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={700}
                            >
                              PUBLIC CONTACT
                            </Typography>
                            {draft.contact_phone && (
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {draft.contact_phone}
                              </Typography>
                            )}
                            {draft.contact_email && (
                              <Typography variant="body2" color="primary.main">
                                {draft.contact_email}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {galleryPreview.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={600}
                            >
                              Gallery
                            </Typography>
                            <Box
                              sx={{
                                mt: 0.75,
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(3, minmax(0, 1fr))",
                                gap: 1,
                              }}
                            >
                              {galleryPreview.map((photo) => (
                                <Box
                                  key={photo.id}
                                  component="img"
                                  src={photo.file}
                                  alt={`${draft.name} gallery`}
                                  sx={{
                                    width: "100%",
                                    height: 70,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Box>

                  <Box>
                    <Typography fontWeight={700}>
                      Publishing readiness
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      Final checks before this venue is published.
                    </Typography>

                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: readyToPublish
                          ? "#F1F8F3"
                          : "background.paper",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          mb: 2,
                        }}
                      >
                        {readyToPublish ? (
                          <CheckCircle color="success" />
                        ) : (
                          <WarningAmber color="warning" />
                        )}
                        <Box>
                          <Typography
                            fontWeight={700}
                            color={
                              readyToPublish ? "success.main" : "warning.main"
                            }
                          >
                            {readyToPublish
                              ? "Ready to publish"
                              : "Needs attention"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {readyToPublish
                              ? "All required information and media are ready."
                              : `${readiness.filter((item) => !item.optional && !item.valid).length} item(s) need attention.`}
                          </Typography>
                        </Box>
                      </Box>

                      <Stack spacing={0.75}>
                        {readiness.map((item) => (
                          <Box
                            key={item.name}
                            sx={{
                              p: 1,
                              borderRadius: 1,
                              bgcolor: "background.paper",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ minWidth: 0 }}
                            >
                              {item.optional && item.value === "Not shown" ? (
                                <RadioButtonUnchecked
                                  sx={{ fontSize: 17, color: "text.disabled" }}
                                />
                              ) : item.valid ? (
                                <CheckCircle
                                  color="success"
                                  sx={{ fontSize: 17 }}
                                />
                              ) : (
                                <WarningAmber
                                  color="warning"
                                  sx={{ fontSize: 17 }}
                                />
                              )}
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {item.name}
                                  {item.optional ? " (optional)" : ""}
                                </Typography>
                                {!item.valid && !item.optional && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {item.source === "venue"
                                      ? "Fix this in Venue Details."
                                      : "Return to the relevant publishing step."}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                            <Typography
                              variant="caption"
                              color={
                                item.optional && item.value === "Not shown"
                                  ? "text.disabled"
                                  : item.valid
                                    ? "text.secondary"
                                    : "warning.main"
                              }
                              textAlign="right"
                              sx={{ flexShrink: 0 }}
                            >
                              {item.value}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>

                    <Alert severity="info" sx={{ mt: 2 }}>
                      {publication.is_published
                        ? "Saving changes updates this listing on the Muzukuru website immediately."
                        : "Publishing makes this venue visible on the Muzukuru website."}
                    </Alert>
                  </Box>
                </Box>
              )}
            </DialogContent>

            {/* STICKY FOOTER */}
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: 1.5,
                bgcolor: "background.paper",
                borderTop: 1,
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              <Box>
                {step === 0 ? (
                  <Button
                    color="inherit"
                    disabled={saving}
                    onClick={handleClose}
                    sx={{ textTransform: "none" }}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button
                    color="inherit"
                    disabled={saving}
                    startIcon={<ArrowBack />}
                    onClick={() => goToStep(step - 1)}
                    sx={{ textTransform: "none" }}
                  >
                    {step === 1 ? "Back" : "Back to Step 2"}
                  </Button>
                )}
              </Box>

              <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
                {step < 2 && (
                  <>
                    <Button
                      variant="text"
                      startIcon={<SaveOutlined />}
                      disabled={saving}
                      onClick={() => void submit("save")}
                      sx={{ textTransform: "none" }}
                    >
                      {saving
                        ? "Saving…"
                        : publication.is_published
                          ? "Save live changes"
                          : "Save draft"}
                    </Button>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForward />}
                      disabled={saving}
                      onClick={() => goToStep(step + 1)}
                      sx={{ textTransform: "none" }}
                    >
                      {step === 0 ? "Continue to photos" : "Continue to review"}
                    </Button>
                  </>
                )}

                {step === 2 && publication.is_published && (
                  <>
                    <Button
                      color="error"
                      disabled={saving}
                      onClick={() => setUnpublishDialogOpen(true)}
                      sx={{ textTransform: "none" }}
                    >
                      Unpublish
                    </Button>
                    {publication.public_url &&
                      /^https?:\/\//i.test(publication.public_url) && (
                        <Button
                          variant="outlined"
                          startIcon={<Launch />}
                          href={publication.public_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: "none" }}
                        >
                          View website
                        </Button>
                      )}
                    <Button
                      variant="contained"
                      startIcon={<SaveOutlined />}
                      disabled={saving || !readyToPublish}
                      onClick={() => void submit("save")}
                      sx={{ textTransform: "none" }}
                    >
                      {saving ? "Saving…" : "Save live changes"}
                    </Button>
                  </>
                )}

                {step === 2 && !publication.is_published && (
                  <>
                    <Button
                      variant="text"
                      startIcon={<SaveOutlined />}
                      disabled={saving}
                      onClick={() => void submit("save")}
                      sx={{ textTransform: "none" }}
                    >
                      {saving ? "Saving…" : "Save draft"}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<PublicOutlined />}
                      disabled={saving || !readyToPublish}
                      onClick={() => void submit("publish")}
                      sx={{ textTransform: "none" }}
                    >
                      {saving ? "Publishing…" : "Publish venue"}
                    </Button>
                  </>
                )}
              </Stack>
            </Box>
          </>
        )}
      </Dialog>

      {/* UNPUBLISH CONFIRMATION */}
      <Dialog
        open={unpublishDialogOpen}
        onClose={() => {
          if (!saving) {
            setUnpublishDialogOpen(false);
          }
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Unpublish {publication.name}?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            This venue will be removed from the Muzukuru website. The original
            venue will remain in MMS.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={saving}
            onClick={() => setUnpublishDialogOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={saving}
            onClick={() => void submit("unpublish")}
            sx={{ textTransform: "none" }}
          >
            {saving ? "Unpublishing…" : "Unpublish venue"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* =========================================================
   COMPACT STEPPER
   ========================================================= */

function PublicationStepper({
  step,
  maxVisitedStep,
  onStepChange,
}: {
  step: number;
  maxVisitedStep: number;
  onStepChange: (step: number) => void;
}) {
  return (
    <Box
      sx={{
        mt: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflowX: "auto",
        py: 0.5,
      }}
    >
      {STEPS.map((label, index) => {
        const active = step === index;
        const completed = step > index;
        const accessible = index <= maxVisitedStep;

        return (
          <Fragment key={label}>
            <ButtonBase
              disabled={!accessible}
              onClick={() => onStepChange(index)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                borderRadius: 1.5,
                px: 0.75,
                py: 0.5,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: completed
                    ? "success.main"
                    : active
                      ? "primary.main"
                      : "action.hover",
                  color: completed || active ? "#fff" : "text.secondary",
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                {completed ? <Check sx={{ fontSize: 16 }} /> : index + 1}
              </Box>

              <Box sx={{ textAlign: "left" }}>
                <Typography
                  sx={{
                    fontSize: 8.5,
                    lineHeight: 1.1,
                    fontWeight: 700,
                    color: active
                      ? "primary.main"
                      : completed
                        ? "success.main"
                        : "text.secondary",
                    textTransform: "uppercase",
                  }}
                >
                  Step {index + 1}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 10, sm: 12 },
                    lineHeight: 1.3,
                    fontWeight: active ? 700 : 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </Typography>
              </Box>
            </ButtonBase>

            {index < STEPS.length - 1 && (
              <Box
                sx={{
                  width: { xs: 20, sm: 58 },
                  height: 2,
                  mx: { xs: 0.25, sm: 1 },
                  borderRadius: 2,
                  bgcolor: completed ? "success.main" : "divider",
                  flexShrink: 0,
                }}
              />
            )}
          </Fragment>
        );
      })}
    </Box>
  );
}

/* =========================================================
   READ-ONLY ITEM
   ========================================================= */

function ReadOnlyItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{
          display: "block",
          mb: 0.4,
          textTransform: "uppercase",
          letterSpacing: 0.35,
        }}
      >
        {label}
      </Typography>
      <Typography
        component="div"
        variant="body2"
        fontWeight={600}
        sx={{ overflowWrap: "anywhere" }}
      >
        {value}
      </Typography>
    </Box>
  );
}
